<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { MessageCircle, X } from 'lucide-vue-next'
import wechatQr from '../assets/wechat-service-qr.png'

const open = ref(false)
const contact = ref(null)

function toggleContact() {
  open.value = !open.value
}

function closeContact() {
  open.value = false
}

function handleOutside(event) {
  if (open.value && contact.value && !contact.value.contains(event.target)) {
    closeContact()
  }
}

function handleEscape(event) {
  if (event.key === 'Escape') closeContact()
}

onMounted(() => {
  document.addEventListener('pointerdown', handleOutside)
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutside)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <aside ref="contact" :class="['floating-contact', { open }]" aria-label="联系客服">
    <section id="wechat-contact-panel" class="contact-panel" aria-live="polite">
      <button class="contact-close" type="button" aria-label="关闭客服二维码" @click="closeContact">
        <X :size="18" />
      </button>
      <p class="contact-kicker">外贸365客服</p>
      <h2>微信扫码咨询</h2>
      <p class="contact-copy">课程、会员和企业服务问题，都可以联系我们。</p>
      <div class="contact-qr-frame">
        <img :src="wechatQr" alt="外贸365客服微信二维码" />
      </div>
      <p class="contact-hint">使用微信扫一扫添加客服</p>
    </section>

    <button
      class="contact-trigger"
      type="button"
      aria-controls="wechat-contact-panel"
      :aria-expanded="open"
      @click="toggleContact"
    >
      <MessageCircle :size="21" />
      <span>联系客服</span>
    </button>
  </aside>
</template>

<style scoped>
.floating-contact {
  position: fixed;
  right: max(24px, env(safe-area-inset-right));
  bottom: max(34px, env(safe-area-inset-bottom));
  z-index: 70;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.contact-trigger {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 18px;
  border: 1px solid #082756;
  border-radius: 4px;
  background: #082756;
  color: #f8fbff;
  box-shadow: 0 14px 34px rgba(8, 39, 86, 0.2);
  font-weight: 750;
  transition: transform 180ms ease, background-color 180ms ease;
}

.contact-trigger:hover,
.contact-trigger:focus-visible {
  background: #0b356f;
  transform: translateY(-2px);
}

.contact-trigger:focus-visible,
.contact-close:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--blue) 44%, transparent);
  outline-offset: 3px;
}

.contact-panel {
  position: relative;
  width: min(256px, calc(100vw - 32px));
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--surface);
  box-shadow: 0 22px 58px rgba(8, 30, 65, 0.18);
  color: var(--ink);
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px);
  pointer-events: none;
  transition: opacity 180ms ease, transform 180ms ease, visibility 180ms ease;
}

.floating-contact:hover .contact-panel,
.floating-contact.open .contact-panel {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
}

.contact-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: var(--muted);
}

.contact-kicker {
  margin: 0 38px 5px 0;
  color: var(--blue);
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.12em;
}

.contact-panel h2 {
  margin: 0;
  font-size: 22px;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.contact-copy {
  margin: 9px 0 16px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.65;
}

.contact-qr-frame {
  padding: 10px;
  border: 1px solid var(--line);
  background: #f8fbff;
}

.contact-qr-frame img {
  width: 100%;
  display: block;
  aspect-ratio: 1;
  object-fit: contain;
}

.contact-hint {
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 12px;
  text-align: center;
}

@media (max-width: 720px) {
  .floating-contact {
    right: max(14px, env(safe-area-inset-right));
    bottom: max(18px, env(safe-area-inset-bottom));
  }

  .contact-trigger {
    min-width: 48px;
    min-height: 48px;
    padding: 0 14px;
  }

  .contact-panel {
    width: min(238px, calc(100vw - 28px));
    padding: 18px;
  }
}

@media (hover: none) {
  .floating-contact:not(.open) .contact-panel {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-trigger,
  .contact-panel {
    transition: none;
  }
}

@media print {
  .floating-contact {
    display: none;
  }
}
</style>
